import { useMemo, type ReactElement } from 'react';
import { Target, TrendingUp, Sparkles, Trophy } from 'lucide-react';
import { Tooltip, Progress } from 'antd';
import { useTranslation } from '@govard/i18n-core/react';
import type { DealListItem } from '../api/deals.client.js';
import { MONO_NUMERIC_STYLE } from '../../../shared/ui/list-styles.js';

interface RevenueGoalBarProps {
  deals: DealListItem[];
  targetAmount?: number;
}

export function RevenueGoalBar({
  deals,
  targetAmount = 1000000,
}: RevenueGoalBarProps): ReactElement {
  const { t, locale } = useTranslation();

  const metrics = useMemo(() => {
    let wonAmount = 0;
    let negotiationAmount = 0;
    let pipelineAmount = 0;
    let wonCount = 0;
    let lostCount = 0;

    for (const deal of deals) {
      const rawAmt = deal.amount;
      const parsedAmt = typeof rawAmt === 'string' ? Number(rawAmt) : (rawAmt ?? 0);
      const amt = Number.isFinite(parsedAmt) ? parsedAmt : 0;

      if (deal.stage === 'Won') {
        wonAmount += amt;
        wonCount++;
      } else if (deal.stage === 'Lost') {
        lostCount++;
      } else {
        pipelineAmount += amt;
        if (deal.stage === 'Negotiation') {
          negotiationAmount += amt;
        }
      }
    }

    const totalFinished = wonCount + lostCount;
    const winRate = totalFinished > 0 ? Math.round((wonCount / totalFinished) * 100) : 75;
    const progressPercent = Math.min(Math.round((wonAmount / targetAmount) * 100), 100);

    // AI Forecast = Won + 80% of Negotiation + 35% of other pipeline
    const aiForecast = Math.round(
      wonAmount + negotiationAmount * 0.8 + (pipelineAmount - negotiationAmount) * 0.35,
    );

    return {
      wonAmount,
      targetAmount,
      progressPercent,
      pipelineAmount,
      aiForecast,
      winRate,
    };
  }, [deals, targetAmount]);

  const intlLocale = locale === 'en' ? 'en-US' : 'ru-RU';
  const formattedWon = new Intl.NumberFormat(intlLocale).format(metrics.wonAmount);
  const formattedTarget = new Intl.NumberFormat(intlLocale).format(metrics.targetAmount);
  const formattedForecast = new Intl.NumberFormat(intlLocale).format(metrics.aiForecast);

  return (
    <div
      data-testid="crm-revenue-goal-bar"
      className="flex flex-wrap items-center justify-between gap-3 rounded-md px-4 py-3 text-xs"
      style={{
        // Theme-aware warm gradient: amber-soft → surface → surface-2 in
        // light, deep-stone with an amber hint in dark (both flip via tokens).
        background:
          'linear-gradient(to right, var(--ant-color-primary-bg) 0%, var(--ant-color-bg-container) 45%, var(--g-fill, #f5f1eb) 100%)',
        border: '1px solid var(--ant-color-border-secondary)',
      }}
    >
      {/* Target & Progress */}
      <div className="flex min-w-[240px] flex-1 items-center gap-3">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-amber-200 bg-amber-100/70 text-amber-800">
          <Trophy size={18} strokeWidth={1.5} />
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between text-[11px] mb-1">
            <span className="font-semibold text-stone-900 flex items-center gap-1.5">
              <Target size={12} className="text-amber-600" />
              <span>{t('crm.revenue_goal.title')}</span>
            </span>
            <span className="font-bold text-amber-900" style={MONO_NUMERIC_STYLE}>
              {formattedWon} / {formattedTarget} ₽ ({metrics.progressPercent}%)
            </span>
          </div>

          <Progress
            percent={metrics.progressPercent}
            showInfo={false}
            strokeColor={{
              '0%': 'var(--ant-color-primary-border-hover)',
              '100%': '#10b981',
            }}
            railColor="var(--ant-color-border-secondary, #e7e5e4)"
            size={['100%', 6]}
          />
        </div>
      </div>

      {/* Badges: AI Forecast & Win Rate */}
      <div className="flex items-center gap-2 shrink-0">
        <Tooltip
          title={
            <div className="text-xs space-y-1 p-1">
              <p className="font-semibold">{t('crm.revenue_goal.forecast_title')}</p>
              <p className="text-[11px] opacity-[0.85]">
                {t('crm.revenue_goal.forecast_desc', { amount: formattedForecast })}
              </p>
            </div>
          }
        >
          <div className="flex items-center gap-1.5 rounded-md border border-[var(--ant-color-primary-border)] bg-[var(--ant-color-bg-container)] px-3 py-1.5 text-xs font-semibold text-amber-900 cursor-help">
            <Sparkles size={13} className="text-amber-600 shrink-0" />
            <span className="text-stone-500 font-normal">
              {t('crm.revenue_goal.forecast_badge')}:
            </span>
            <span className="text-amber-800" style={MONO_NUMERIC_STYLE}>
              {formattedForecast} ₽
            </span>
          </div>
        </Tooltip>

        <div className="flex items-center gap-1.5 rounded-md border border-[var(--ant-color-success-border)] bg-[var(--ant-color-success-bg)] px-2.5 py-1.5 text-xs font-semibold text-[var(--ant-color-success-text)]">
          <TrendingUp size={13} className="shrink-0" />
          <span>
            {t('crm.revenue_goal.win_rate')}: {metrics.winRate}%
          </span>
        </div>
      </div>
    </div>
  );
}
